import { useEffect, useContext } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Loader, Message } from '@components';
import {
  FormFieldset,
  SelectField,
  TextField,
  ToggleField,
} from '@Forms/fields.jsx';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';
import { mapGroupRoles, GROUP_ROLES, getGroupSettings } from '@helpers';
import { GlobalContext } from '@contexts';

const GroupForm = ({
  mutation,
  onCancel,
  groupData,
  isPending,
  type = 'new',
}) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setFocus,
    setError,
    reset,
  } = useForm({
    defaultValues: {
      name: groupData?.name || '',
      role: groupData?.roles?.[0] || '',
      dashboardVisibility: {
        locationSocialView: false,
        siteBlogsView: false,
        siteBlogCategoriesView: false,
        siteBlogAuthorsView: false,
        sitePagesView: false,
      },
    },
    mode: 'onBlur',
  });
  const { isDirty, isValid } = useFormState({ control });
  const globals = useContext(GlobalContext);

  // check if every key in object has a value
  const allowNoBasePerms = Object.values(
    globals.whitelabel.oneoff_access_group_ids || {},
  ).every((value) => !!value);

  const selectedRole = useWatch({ control, name: 'role' });

  useEffect(() => {
    setFocus('name');
  }, [setFocus]);

  useEffect(() => {
    if (groupData) {
      reset({
        name: groupData.name || '',
        role: groupData.roles?.[0] || '',
        dashboardVisibility: {
          locationSocialView:
            groupData.settings?.dashboardVisibility?.locationSocialView,
          siteBlogsView: groupData.settings?.dashboardVisibility?.siteBlogsView,
          siteBlogCategoriesView:
            groupData.settings?.dashboardVisibility?.siteBlogCategoriesView,
          siteBlogAuthorsView:
            groupData.settings?.dashboardVisibility?.siteBlogAuthorsView,
          sitePagesView: groupData.settings?.dashboardVisibility?.sitePagesView,
        },
      });
    }
  }, [groupData, reset]);

  const onSubmit = (data) => {
    const payload = {
      name: data.name,
      roles: data.role ? [data.role] : [],
      settings: getGroupSettings(data),
    };

    mutation.mutate(payload, {
      onError: (error) => {
        const errorsList = errorsFromMutate(error);
        errorsList.forEach(({ field, error }) => setError(field, error));
      },
      onSuccess: () => {
        // refresh list and return to groups screen
        queryClient.invalidateQueries({ queryKey: ['groups', 'list'] });
      },
    });
  };

  // if the group is new or the group being edited is not access role, hide the "resitricted users" role. Otherwise show it.
  const roleOptions = allowNoBasePerms
    ? Object.entries(GROUP_ROLES)
    : Object.entries(GROUP_ROLES).filter(([key]) => key !== 'no_base_perms');

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` +
        (mutation.isPending || isPending ? 'sb-form-disabled' : '')
      }
    >
      {isPending && <Loader className="sb-loader sb-loader-large" />}
      {!isPending && (
        <FormFieldset>
          <TextField
            register={register}
            error={errors.name}
            options={{ required: 'This field is required' }}
            name="name"
            label="Group name"
            placeholder="Add a group name"
          />
          <SelectField
            register={register}
            error={errors.role}
            name="role"
            label="Role"
            helpText="Roles define the permissions assigned to this group."
          >
            <option value="">No role</option>
            {roleOptions.map(([key, value]) => {
              return (
                <option key={key} value={key}>
                  {value}
                </option>
              );
            })}
          </SelectField>

          {selectedRole === 'no_base_perms' && (
            <>
              <ToggleField
                register={register}
                control={control}
                name="dashboardVisibility.locationSocialView"
                label="Can manage Location social media"
              />
              <ToggleField
                register={register}
                control={control}
                name="dashboardVisibility.siteBlogsView"
                label="Can manage Blog posts"
              />
              <ToggleField
                register={register}
                control={control}
                name="dashboardVisibility.siteBlogCategoriesView"
                label="Can manage Blog categories"
              />
              <ToggleField
                register={register}
                control={control}
                name="dashboardVisibility.siteBlogAuthorsView"
                label="Can manage Blog authors"
              />
              {globals.whitelabel.oneoff_access_group_ids.TemplateSite && (
                <ToggleField
                  register={register}
                  control={control}
                  name="dashboardVisibility.sitePagesView"
                  label="Can manage Templated pages"
                  helpText="Note: requires additional access settings to templates."
                />
              )}
            </>
          )}
        </FormFieldset>
      )}
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {onCancel && (
            <button
              type="button"
              className="sb-btn-cancel sb-btn"
              onClick={onCancel}
              disabled={mutation.isPending}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={mutation.isPending || isPending}
          >
            {mutation.isPending
              ? 'Saving...'
              : type === 'new'
                ? 'Add group'
                : 'Save group'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error saving changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully saved" />
        )}
      </div>
    </form>
  );
};

export default GroupForm;
